import { useMemo, useState } from 'react'

import type { MiProgramacion } from '@/lib/supabase/tipos-bd'
import { Aviso } from '@/shared/ui/Aviso/Aviso'
import { Cargando } from '@/shared/ui/Estados/Cargando'

import { useMiProgramacion } from './useMiProgramacion'
import estilos from './PaginaMiProgramacion.module.css'

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]
const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

/** El día en Bogotá (aaaa-mm-dd) de un instante. */
function diaBogota(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Bogota',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso))
}

function tituloDelDia(dia: string): string {
  const [a, m, d] = dia.split('-').map(Number)
  const fecha = new Date(a, m - 1, d)
  const semana = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado']
  return `${semana[fecha.getDay()]}, ${d} de ${MESES[m - 1]}`
}

function hora(iso: string): string {
  return new Intl.DateTimeFormat('es-CO', {
    timeZone: 'America/Bogota',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(iso))
}

/**
 * La programación del operario, en calendario. Solo para mirar.
 *
 * Un mes a la vez: los días con trabajo van marcados, y al tocar uno se ve qué
 * tiene ese día. No se puede modificar nada; quién programa es la oficina.
 */
export function PaginaMiProgramacion() {
  const { programacion, cargando, error } = useMiProgramacion()

  const porDia = useMemo(() => {
    const mapa = new Map<string, MiProgramacion[]>()
    for (const p of programacion) {
      const dia = diaBogota(p.fecha_inicio_programada)
      const lista = mapa.get(dia) ?? []
      lista.push(p)
      mapa.set(dia, lista)
    }
    return mapa
  }, [programacion])

  const hoy = diaBogota(new Date().toISOString())
  const [mesAncla, setMesAncla] = useState(() => hoy.slice(0, 7)) // aaaa-mm
  const [seleccion, setSeleccion] = useState(hoy)

  const [anio, mes] = mesAncla.split('-').map(Number)
  const celdas = useMemo(() => {
    const primero = new Date(anio, mes - 1, 1)
    const dias = new Date(anio, mes, 0).getDate()
    const inicio = (primero.getDay() + 6) % 7 // lunes primero
    const lista: (string | null)[] = Array.from({ length: inicio }, () => null)
    for (let d = 1; d <= dias; d++) {
      lista.push(`${mesAncla}-${String(d).padStart(2, '0')}`)
    }
    return lista
  }, [anio, mes, mesAncla])

  function moverMes(delta: number) {
    const nuevo = new Date(anio, mes - 1 + delta, 1)
    setMesAncla(`${nuevo.getFullYear()}-${String(nuevo.getMonth() + 1).padStart(2, '0')}`)
  }

  const delDia = (porDia.get(seleccion) ?? []).slice().sort((a, b) =>
    a.fecha_inicio_programada.localeCompare(b.fecha_inicio_programada),
  )

  if (cargando) return <Cargando mensaje="SINCRONIZANDO PROGRAMACIÓN TÉCNICA..." />
  if (error) return <Aviso tipo="error">{error}</Aviso>

  return (
    <div className={estilos.pagina}>
      <header className={estilos.mes}>
        <button type="button" className={estilos.flecha} onClick={() => moverMes(-1)} aria-label="Mes anterior">
          ‹
        </button>
        <span className={estilos.mesNombre}>
          {MESES[mes - 1]} {anio}
        </span>
        <button type="button" className={estilos.flecha} onClick={() => moverMes(1)} aria-label="Mes siguiente">
          ›
        </button>
      </header>

      <div className={estilos.semana}>
        {DIAS.map((d, i) => (
          <span key={i} className={estilos.diaSemana}>
            {d}
          </span>
        ))}
      </div>

      <div className={estilos.rejilla}>
        {celdas.map((dia, i) =>
          dia === null ? (
            <span key={`v${i}`} />
          ) : (
            <button
              key={dia}
              type="button"
              className={`${estilos.celda} ${dia === hoy ? estilos.hoy : ''} ${
                dia === seleccion ? estilos.elegido : ''
              }`}
              onClick={() => setSeleccion(dia)}
            >
              {Number(dia.slice(-2))}
              {porDia.has(dia) && <span className={estilos.marca} aria-hidden="true" />}
            </button>
          ),
        )}
      </div>

      <section className={estilos.detalle}>
        <p className={estilos.detalleTitulo}>{tituloDelDia(seleccion)}</p>

        {delDia.length === 0 ? (
          <p className={estilos.sinNada}>Sin trabajo programado ese día.</p>
        ) : (
          delDia.map((t) => (
            <article
              key={`${t.tipo ?? 'trabajo'}-${t.orden_trabajo_id}`}
              className={`${estilos.trabajo} ${t.tipo === 'visita' ? estilos.visita : ''}`}
            >
              <div className={estilos.trabajoTope}>
                <span className={estilos.hora}>{hora(t.fecha_inicio_programada)}</span>
                {/* Las visitas (0256) van marcadas: no son una obra, son ir a
                    mirar y poner precio. */}
                {t.tipo === 'visita' && (
                  <span className={estilos.etiquetaVisita}>
                    {t.estado === 'finalizada' ? 'Visita hecha' : 'Visita'}
                  </span>
                )}
                <span className={estilos.numero}>{t.numero}</span>
                {t.horas_asignadas !== null && (
                  <span className={estilos.horas}>{t.horas_asignadas} h</span>
                )}
              </div>
              <p className={estilos.cliente}>{t.cliente ?? 'Sin cliente'}</p>
              <p className={estilos.direccion}>
                {t.direccion}
                {t.zona && <span className={estilos.zona}> · {t.zona}</span>}
              </p>
              {(t.servicio_nombre || t.servicio_codigo) && (
                <p className={estilos.servicio}>
                  {t.servicio_codigo && <span className={estilos.codigo}>{t.servicio_codigo}</span>}
                  {t.servicio_nombre}
                </p>
              )}
            </article>
          ))
        )}
      </section>
    </div>
  )
}
